import Vimeo from "@u-wave/react-vimeo";
import YouTube from "react-youtube";
import { AnimatePresence, motion } from "framer-motion";
import { getAssetPath, getVideoProviderAndCode } from "utils";
import { render } from "react-dom";
import { SyntheticEvent, useEffect, useState } from "react";

const VideoPlayer = ({
  poster,
  code,
  provider,
}: {
  poster: string;
  code: string;
  provider: string;
}) => {

  const [YTPlayer, setYTPlayer] = useState<any>(); // needed to store the YT player object & methods
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const toggleVideoPlayer = () => {
    if (provider === "youtube") YTPlayer.playVideo();
    setIsPlaying(true);
  };

  return (
    <div>
      <AnimatePresence>
        {!isPlaying && (
          <motion.div
            key={code}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundImage: `url(${poster})`,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="95"
              height="95"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={toggleVideoPlayer}
              style={{ cursor: "pointer" }}
              className="playbutton"
            >
              <motion.path
                d="M47.5 95C73.734 95 95 73.734 95 47.5 95 21.267 73.734 0 47.5 0 21.267 0 0 21.267 0 47.5 0 73.734 21.267 95 47.5 95Z"
                fill="#84BD00"
              />
              <g className="playicon">
                <path
                  d="M70.109 46.281a2 2 0 0 1 0 3.438L39.022 68.2A2 2 0 0 1 36 66.484V29.516a2 2 0 0 1 3.022-1.716l31.087 18.481Z"
                  fill="#fff"
                />
              </g>
            </svg>
          </motion.div>

        )
        }
      </AnimatePresence >

      <div style={{ position: "static", zIndex: 100 }}>
        {provider === "youtube" && (
          <YouTube
            opts={{
              playerVars: {
                showinfo: 0,
                modestbranding: 1,
                rel: 0,
              },
            }}
            videoId={code}
            onReady={(e: SyntheticEvent) => setYTPlayer(e.target)}
          />
        )}

        {provider === "vimeo" && <Vimeo video={code} paused={!isPlaying} />}
      </div>
    </div >
  );
};

/**
 * This hook will
 * 1. make an array of all `.video-container` elements on the page
 * 2. extra the poster image (required to run)
 * 3. extract the video shortcode
 * 4. replace the contents of the element with a custom player
 */
export const useVideoWrapper = () => {
  useEffect(() => {
    const videoContainers = Array.from(
      document.querySelectorAll(".video-container")
    );

    if (videoContainers.length) {
      videoContainers.forEach((video) => {
        video.classList.add('animateonscroll');
        
        const videoURL =
          video.querySelector("iframe")?.getAttribute("src") || undefined;

        // get the URL to the image to be used as the poster
        let poster = getAssetPath(
          video?.querySelector("img.poster")?.getAttribute("src") || "", true
        );

        // if we dont have a poster in sitecore, fetch the thumbnail from youtube
        if (
          !poster &&
          videoURL &&
          getVideoProviderAndCode(videoURL).provider === "youtube"
        )
          poster = `https://img.youtube.com/vi/${getVideoProviderAndCode(videoURL).code
            }/maxresdefault.jpg`;

        // if we dont have a poster in sitecore, fetch the thumbnail from vimeo
        if (
          !poster &&
          videoURL &&
          getVideoProviderAndCode(videoURL).provider === "vimeo"
        )
          poster = `http://ts.vimeo.com.s3.amazonaws.com/235/662/${getVideoProviderAndCode(videoURL).code
            }.jpg`;

        if (poster && videoURL) {
          return render(
            <VideoPlayer
              poster={poster}
              {...getVideoProviderAndCode(videoURL)}
            />,
            video
          );
        }
      });
    }
  }, []);
};
